import type { TableRowDefinition } from "$components/table";
import { parseISO } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";
import type { IntlShape } from "react-intl";
import type {
  DiagnosisReceived,
  InvestigationPerformed,
  InvestigationRequested,
  PrescriptionIssued,
  RegisterRecord,
} from "../../../../__generated__/base.gql";
import { CellWithSubscript } from "../../_lib/cell-with-subscript";
import { getAge } from "../../_lib/get-age";

export function createRowFormatter(
  timeZone: string,
  formatMessage: IntlShape["formatMessage"],
) {
  return (record: RegisterRecord): TableRowDefinition => {
    const age = getAge(
      { date: record.date, dateOfBirth: record.patient.dateOfBirth },
      formatMessage,
    );

    const diagnoses = record.careActivities
      ?.filter(
        (c) => (c as DiagnosisReceived).__typename === "DiagnosisReceived",
      )
      .map((c) => (c as DiagnosisReceived)?.careActivity?.name)
      .join(", ");

    const investigationsPerformed =
      record.careActivities?.filter(
        (c) =>
          (c as InvestigationPerformed).__typename === "InvestigationPerformed",
      ) ?? [];

    const investigationsRequested =
      record.careActivities?.filter(
        (c) =>
          (c as InvestigationRequested).__typename === "InvestigationRequested",
      ) ?? [];

    const performedIds = new Set(
      investigationsPerformed.map(
        (c) => (c as InvestigationPerformed)?.careActivity?.investigationId,
      ),
    );

    const investigations = [
      ...investigationsRequested
        .filter(
          (i) =>
            !performedIds.has(
              (i as InvestigationRequested)?.careActivity?.investigationId,
            ),
        )
        .filter((i) => !!i),
      ...investigationsPerformed,
    ]
      .map((i) => (i as InvestigationPerformed)?.careActivity?.name)
      .join(", ");

    const prescriptions = record?.careActivities
      ?.filter(
        (c) => (c as PrescriptionIssued).__typename === "PrescriptionIssued",
      )
      .map((c) => (c as PrescriptionIssued)?.careActivity?.medication?.name)
      .join(", ");

    const date = utcToZonedTime(parseISO(record.date), timeZone);
    return {
      id: record._id,
      patientUuid: record.patient.patientUuid,
      date: CellWithSubscript({
        script: format(date, "dd MMM", { timeZone }),
        subscript: format(date, "yyyy", { timeZone }),
      }),
      name: record.patient.name,
      phone: CellWithSubscript({
        script: record.patient?.phoneNumber?.value ?? "",
        subscript: record.patient?.phoneNumber?.ownership ?? "",
      }),
      sex: (record.patient.gender ?? "").toUpperCase(),
      age,
      address: `${record.patient?.address?.street}, ${record.patient?.address?.ward}, ${record.patient?.address?.subCounty}, ${record.patient?.address?.state}`,
      diagnoses,
      investigations,
      prescriptions,
    };
  };
}
